import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, List, Toast } from 'antd-mobile-v5'
import { WhiteSpace } from 'antd-mobile'
import InvoiceInfo from '../PriceList/InvoiceInfo'
import { FormattedMessage, useIntl } from 'react-intl'
import OrderItem from '../PriceList/OrderItem'
import PriceSummary from '../PriceList/PriceSummary'
import {
  useGetPicOrderDetail,
  useTextbookCloseOrder,
  useTextbookOrderPayment,
  useTextbookPaymentStatus,
} from '../../services/hooks'
import {
  getParameterByName,
  isOrderUnderAmountReview,
  showAlert,
  textbooksRegions,
} from '../../components/common/utils'
import {
  createWposPayTypePicker,
  FUIOU_H5_PAY_TYPE,
  isValidFuiouUrl,
  isValidWposQrContent,
  pickFuiouChannel,
  safeExternalRedirect,
  safeFuiouRedirect,
} from '../../components/FormTools/utils'
import FuiouQrPopup from '../../components/FormTools/FuiouQrPopup'
import { useLanguage } from '../../services/hooks/user'
import { getWechatOpenId } from '../../utils/wechat'

const RegionSection = ({ detail }) => {
  const lang = useLanguage()

  const findAddressType = (userAddressType) => {
    const addressTypeList = textbooksRegions[lang]
    const currentAddressType = addressTypeList.find((type) => type.value === userAddressType)
    return currentAddressType.label
  }

  return (
    <List header={<FormattedMessage id="common-address-detail" />}>
      <List.Item extra={findAddressType(detail.userAddressType)}>
        <FormattedMessage id="addressForm-region" />
      </List.Item>
      <List.Item extra={detail.userAddress}>
        <FormattedMessage id="addressForm-address" />
      </List.Item>
      <List.Item extra={detail.userName}>
        <FormattedMessage id="addressForm-contact" />
      </List.Item>
      <List.Item extra={detail.userMobile}>
        <FormattedMessage id="addressForm-mobile" />
      </List.Item>
    </List>
  )
}

/**
 * 下单备注（选填）。未填写时后端返回 null —— 整块不渲染，不给空行占版面。
 */
const RemarkSection = ({ remark }) => {
  if (!remark) return null
  return (
    <>
      <WhiteSpace />
      <List header={<FormattedMessage id="order-remark" />}>
        <List.Item>{remark}</List.Item>
      </List>
    </>
  )
}

/**
 * 后台改价提示（CHNA-2755）。未改价（priceAdjusted 为假）时整块不渲染。
 * 改价后实付与「商品 + 运费」口径不再自洽，必须明示是人工调整，
 * 否则用户会把差额当成系统算错价。
 */
const PriceAdjustSection = ({ order }) => {
  if (!order.priceAdjusted) return null
  return (
    <>
      <WhiteSpace />
      <List header={<FormattedMessage id="order-price-adjusted" />}>
        <List.Item>
          <FormattedMessage
            id="order-price-adjusted-detail"
            values={{
              original: `${order.currencyType} ${order.originalPaymentPrice}`,
              current: `${order.currencyType} ${order.totalPayment}`,
            }}
          />
        </List.Item>
      </List>
    </>
  )
}

/**
 * 实收与应收不符、已交人工核对（后端 {@code PaymentMethodType.ERROR} = 0，CHNA-2755）。
 * 这类单后端拒绝重付（getPaymentUrl 报错）也拒绝关闭（closeOrder 保持 WAIT_PAY），
 * 必须说清原因，否则用户会对着一个点了必然报错的「去支付」反复试。
 */
const AmountReviewSection = ({ order }) => {
  if (!isOrderUnderAmountReview(order)) return null
  return (
    <>
      <WhiteSpace />
      <List header={<FormattedMessage id="order-under-review" />}>
        <List.Item>
          <FormattedMessage id="order-under-review-detail" />
        </List.Item>
      </List>
    </>
  )
}

const PaymentBtn = ({ id, currencyType }) => {
  const { formatMessage } = useIntl()
  const wposPickerRef = useRef(null)
  // await picker 后组件可能已卸载：setState 前判 mounted 防卸载告警
  const mountedRef = useRef(true)
  // 合并为单个 state：React 16 无自动批处理，promise 回调里三连 setState 会触发三次渲染、
  // 连带 FuiouQrPopup 的 startPolling effect 多余重启
  const [qrState, setQrState] = useState({ url: '', visible: false, orderId: null, price: null, currency: null })
  // WPOS 渠道选择结果（WECHAT/ALIPAY），传给 FuiouQrPopup 展示对应渠道边框色与文案
  const [qrPayType, setQrPayType] = useState(null)
  const { run, loading } = useTextbookOrderPayment({
    onSuccess: (data) => {
      if (data.error) {
        showAlert(data.error.clientMsg, formatMessage({ id: 'common-close' }))
        return
      }
      const channel = pickFuiouChannel()
      if (data.url && channel === 'WPOS' && currencyType === 'CNY') {
        // PC WPOS 扫码分支：单码通道返回渠道二维码内容（qr.alipay.com / weixin://），
        // 只做本地编码展示，校验用二维码内容白名单（兼容历史富友聚合链接形态）。
        // 页内展示二维码并按订单维度轮询支付状态，不再整页跳转。
        // 仅大陆富友单走扫码（与 toPay 的渠道选择门禁一致）；TWD/AUD 订单后端返回
        // TradeVan/Stripe 整页链接，不属于二维码内容，落到下方 safeExternalRedirect
        if (!isValidWposQrContent(data.url)) {
          Toast.show({ icon: 'fail', content: formatMessage({ id: 'fuiou-pay-url-invalid' }) })
          return
        }
        // CHNA-2618：price 为订单实付总额，currency 随通道变化（此分支仅大陆富友 CNY）
        setQrState({
          url: data.url,
          visible: true,
          orderId: data.orderId || id,
          price: data.price,
          currency: data.currency,
        })
      } else if (data.url && isValidFuiouUrl(data.url)) {
        safeFuiouRedirect(data.url, formatMessage({ id: 'fuiou-pay-url-invalid' }))
      } else if (data.url) {
        // 海外 Stripe/TradeVan 等通道：非富友域名，跳转前校验 http(s) 协议
        safeExternalRedirect(data.url, formatMessage({ id: 'fuiou-pay-url-invalid' }), {
          // 白名单外域名弹确认（message 由 safeExternalRedirect 以 hostname 调用）
          message: (host) => formatMessage({ id: 'fuiou-external-redirect-confirm' }, { host }),
          confirm: formatMessage({ id: 'common-comfirmBtn' }),
          cancel: formatMessage({ id: 'common-cancel' }),
        })
      } else {
        // 下单成功但无 url（异常形态）：提示失败，避免静默无反馈
        Toast.show({ icon: 'fail', content: formatMessage({ id: 'fuiou-pay-fail' }) })
      }
    },
  })

  // 轮询失败时抑制 useRequestCommon 的全局错误 Modal（轮询类请求不弹窗）
  const { runAsync: checkPaymentStatus } = useTextbookPaymentStatus({ manual: true, onError: () => true })

  const checkStatus = useCallback(async () => {
    const result = await checkPaymentStatus(qrState.orderId)
    // hooks 层返回裸 body；与 BooksCheckout 同一端点，已支付判定以订单状态为准——
    // paymentMethod 在订单创建时即写入（支付方式），不能作为支付完成信号。
    // TRADE_PAID（富友支付成功终态）与 PAID 等价：漏判会把已付款订单误判为未支付，
    // 诱导用户重复下单付款（二次扣款）
    return !!(result && ['PAID', 'TRADE_PAID', 'SHIPPED'].includes(result.status))
  }, [checkPaymentStatus, qrState.orderId])

  // 关单只由用户显式「取消订单」触发（弹窗关闭/返回/超时都不再关单，见 FuiouQrPopup）：
  // 失败向上抛，由弹窗静默收起——后端幂等，失败时订单仍是 WAIT_PAY，可再次发起支付
  const { runAsync: closeTextbookOrder } = useTextbookCloseOrder({ manual: true, onError: () => true })
  const cancelOrder = useCallback(async () => {
    if (!qrState.orderId) return
    await closeTextbookOrder(qrState.orderId)
    // 关单成功后整页刷新：详情重新拉取已变 CLOSED、支付按钮消失。不刷新会留着一个 WAIT_PAY 的
    // 「去支付」，点下去必然报错（后端 getPaymentUrl 拒绝非 WAIT_PAY 单）
    window.location.reload()
  }, [closeTextbookOrder, qrState.orderId])

  const handlePaymentSuccess = useCallback(() => {
    setQrState((prev) => ({ ...prev, visible: false }))
    Toast.show({
      icon: 'success',
      content: formatMessage({ id: 'fuiou-pay-success' }),
      // 刷新订单详情：重新拉取后状态由 WAIT_PAY 变为已支付，支付按钮消失
      afterClose: () => window.location.reload(),
    })
  }, [formatMessage])

  // 组件卸载时清理残留的命令式渠道选择面板
  useEffect(() => {
    // StrictMode 下 effect 会 setup→cleanup→setup 重跑：cleanup 置 false 后必须复位，
    // 否则重挂载后 mountedRef 恒 false，支付回调全部失效
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (wposPickerRef.current) {
        wposPickerRef.current.close()
        wposPickerRef.current = null
      }
    }
  }, [])

  // 互斥锁：防双击支付并发建单（与 EcardCheckoutPopup/AgentElearning 一致）
  const payingRef = useRef(false)

  const toPay = async () => {
    if (payingRef.current) return
    payingRef.current = true
    try {
      const channel = pickFuiouChannel()
      let payType = channel === 'H5PAY' ? FUIOU_H5_PAY_TYPE.WECHAT_JSAPI : undefined
      // 大陆富友单 WPOS 必须显式传 payType（FAPPLET 聚合码已下线），先弹渠道选择
      if (currencyType === 'CNY' && channel === 'WPOS') {
        const picker = createWposPayTypePicker(formatMessage)
        wposPickerRef.current = picker
        payType = await picker.promise
        wposPickerRef.current = null
        // 用户取消渠道选择，静默返回
        if (!payType) return
        if (mountedRef.current) setQrPayType(payType)
      }
      run({ id, channel, payType, openid: getWechatOpenId() })
    } finally {
      payingRef.current = false
    }
  }

  return (
    <>
      <WhiteSpace />
      <Button block color="primary" loading={loading} disabled={loading} onClick={toPay}>
        <FormattedMessage id="app-to-pay" />
      </Button>
      <FuiouQrPopup
        visible={qrState.visible}
        url={qrState.url}
        price={qrState.price}
        currency={qrState.currency}
        onClose={() => setQrState((prev) => ({ ...prev, visible: false }))}
        onPaymentSuccess={handlePaymentSuccess}
        checkStatus={checkStatus}
        payType={qrPayType}
        onCancelOrder={cancelOrder}
      />
    </>
  )
}

const PicOrderDetail = () => {
  const id = getParameterByName('id')
  const { loading, data } = useGetPicOrderDetail(id)

  const formatOrderData = () => {
    const { itemDTOS, ...details } = data
    const formatItems = itemDTOS.map((item) => ({ ...item, currencyType: data.currencyType }))
    return { ...details, itemDTOS: formatItems }
  }

  const findOrderPrice = (item, order) => {
    if (!order) return null
    const match = order.itemDTOS.find((i) => item.goodsId === i.goodsId)
    return match ? match.discountPrice : null
  }

  if (!data || loading) return <>loading</>

  return (
    <>
      <RegionSection detail={data} />
      <RemarkSection remark={data.remark} />
      <PriceAdjustSection order={data} />
      <AmountReviewSection order={data} />
      {data.invoiceDTO.affiliateId && <InvoiceInfo invoiceInfo={data.invoiceDTO} disabled={true} />}
      <List header={<FormattedMessage id="items-list" />} className="price-items">
        {formatOrderData().itemDTOS.map((item) => (
          <OrderItem key={item.goodsId} item={item} orderPrice={findOrderPrice(item, data)} />
        ))}
      </List>
      <PriceSummary
        selectedCoupon={false}
        currencyType={formatOrderData().itemDTOS[0].currencyType}
        order={formatOrderData()}
      />
      {data.status === 'WAIT_PAY' && !isOrderUnderAmountReview(data) && (
        <PaymentBtn id={data.id} currencyType={data.currencyType} />
      )}
    </>
  )
}

export default PicOrderDetail
